import React, { useState, useRef } from 'react';
import { MonitoringStatus, getMonitoringStatus } from '../config/thresholds';
import MiniChart from './MiniChart';
import { ResourceDataDto } from '../../../models/Pharse2/ResourceData';
import LocationDetailDialog from './LocationDetailDialog';

interface MonitoringCardProps {
  value: number;
  unit: string;
  location: string;
  locationId?: number;
  timestamp?: string;
  variant?: 'dark' | 'light';
  size?: 'large' | 'small';
  monitoringType?: 'noise' | 'tsp' | 'vibration';
  chartData?: ResourceDataDto[]; // History data for chart (only for vibration)
  alertThreshold?: number | null; // Alert threshold from LocationGroup
  alarmThreshold?: number | null; // Alarm threshold from LocationGroup
  updateInterval?: number; // Update interval in milliseconds
  projectName?: string | null; // Project name for special threshold logic
}

const MonitoringCard: React.FC<MonitoringCardProps> = ({
  value,
  unit,
  location,
  locationId,
  timestamp,
  variant = 'dark',
  size = 'large',
  monitoringType,
  chartData,
  alertThreshold,
  alarmThreshold,
  updateInterval,
  projectName,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // 格式化時間戳為 YYYY/MM/DD,HH:mm 格式
  const formatTimestamp = (timestamp?: string): string => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}/${month}/${day},${hours}:${minutes}`;
    } catch {
      return timestamp;
    }
  };

  // 檢查數據是否過時
  const isDataStale = (): boolean => {
    if (!timestamp) return true; // 沒有時間戳視為過時
    try {
      const dataTime = new Date(timestamp);
      const now = new Date();
      const diffInMs = now.getTime() - dataTime.getTime();

      // 對於 noise 和 tsp：如果超過 updateInterval * 10 + 10秒，則視為過時
      if ((monitoringType === 'noise' || monitoringType === 'tsp') && updateInterval) {
        return diffInMs > updateInterval * 10 + 10000;
      }

      // 對於 vibration：超過 1 分鐘視為過時（保持原有邏輯）
      const diffInMinutes = diffInMs / (1000 * 60);
      return diffInMinutes > 1;
    } catch {
      return true; // 解析失敗視為過時
    }
  };

  const dataStale = isDataStale();

  // 判斷狀態（如果有監控類型），但如果數據過時則不判斷狀態
  const status: MonitoringStatus = dataStale
    ? 'normal' // 數據過時時不顯示狀態顏色，將使用灰色
    : monitoringType
    ? getMonitoringStatus(value, monitoringType, undefined, alertThreshold, alarmThreshold, projectName)
    : 'normal';

  // 根據狀態和變體決定背景顏色
  const getBackgroundColor = () => {
    // 如果數據過時（超過 1 分鐘），使用灰色
    if (dataStale) return 'bg-gray-500'; // 數據過時使用灰色

    // Dark variant - 使用更接近圖片的深藍色
    if (status === 'alarm') return 'bg-red-700'; // Alarm level (red) - 原來的 alert 改名為 alarm
    if (status === 'alert') return 'bg-yellow-600'; // Alert level (yellow) - 新的 alert level
    return 'bg-blue-600'; // 正常時使用深藍色（匹配圖片 #2563EB）
  };

  const getTextColor = () => {
    return 'text-white';
  };

  const baseClasses = variant === 'dark'
    ? `${getBackgroundColor()} ${getTextColor()} shadow-lg relative overflow-hidden`
    : `${getBackgroundColor()} ${getTextColor()} border shadow-md relative overflow-hidden`;

  const sizeClasses = size === 'large'
    ? chartData && chartData.length > 0
      ? 'p-4 min-h-[220px]' // 有圖表時需要更多高度
      : 'p-4 min-h-[160px]'
    : chartData && chartData.length > 0
      ? 'p-3 min-h-[180px]'
      : 'p-3 min-h-[120px]';

  // 淺黃綠色橫條顏色（匹配圖片 - light yellow-green）
  const barColor = dataStale
    ? 'bg-gray-400' // 數據過時使用灰色橫條
    : status === 'alarm'
    ? 'bg-red-400' // Alarm level (red)
    : status === 'alert'
    ? 'bg-yellow-400' // Alert level (yellow)
    : 'bg-lime-300'; // 正常時使用淺黃綠色（lime-300 更接近圖片中的 light yellow-green）

  return (
    <div className={`${baseClasses} ${sizeClasses} rounded-lg flex flex-col justify-center items-center transition-all hover:shadow-xl relative`}>
      {/* 頂部橫條 - 增大高度 */}
      <div className={`${barColor} absolute top-0 left-0 right-0 h-[10px] rounded-t-lg`}></div>

      {/* 底部橫條 - 增大高度 */}
      <div className={`${barColor} absolute bottom-0 left-0 right-0 h-[10px] rounded-b-lg`}></div>

      {/* 記錄時間 - 左上角 */}
      {timestamp && (
        <div className={`absolute top-4 left-2 text-xs opacity-75 font-medium ${getTextColor()} z-20`}>
          {formatTimestamp(timestamp)}
        </div>
      )}

      {/* 詳情按鈕 - 右上角 */}
      {locationId && (
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.stopPropagation();
            setIsDialogOpen(true);
          }}
          className="absolute top-4 right-2 z-30 p-2 bg-gray-800 bg-opacity-70 hover:bg-opacity-100 rounded-full transition-all text-white"
          title="View Location Details"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>
      )}

      {/* 內容區域 */}
      <div className="flex flex-col items-center justify-center z-10 w-full h-full">
        {/* 數值和單位 */}
        <div className={`text-3xl font-bold mb-2 text-center ${getTextColor()}`}>
          {monitoringType === 'noise' || monitoringType === 'tsp' ?
            value.toFixed(1) :
            value.toFixed(3)}
            <div className={`text-1xl font-bold text-center ${getTextColor()}`}> {unit} </div>
        </div>

        {/* 迷你圖表 - 僅在 vibration 且有 chartData 時顯示 */}
        {monitoringType === 'vibration' && chartData && chartData.length > 0 && (
          <div className="w-full h-[100px] mt-2 px-2">
            <MiniChart
              data={chartData}
              unit={unit}
              status={status}
              alertThreshold={alertThreshold}
            />
          </div>
        )}
      </div>

      {/* 位置詳情對話框 */}
      {locationId && (
        <LocationDetailDialog
          locationId={locationId}
          locationName={location}
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          monitoringType={monitoringType}
          currentValue={value}
          timestamp={timestamp}
          buttonRef={buttonRef}
          alertThreshold={alertThreshold}
          alarmThreshold={alarmThreshold}
          unit={unit}
          updateInterval={updateInterval}
          projectName={projectName}
        />
      )}
    </div>
  );
};

export default MonitoringCard;

